import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { Checkbox, Form, Radio } from 'antd';
import shippingService from 'services/shippingService';
import { useOrgId, useOrg } from 'hooks/useOrg';
import { useEffect, useState, useMemo } from 'react';
import {
  OrgShippingAccountTypeDisplay,
  isSaasOrg,
  isCustomerOrg,
} from 'domains/orgDomain';
import { useAppSelector } from 'app/hooks';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';
import { Trans } from 'react-i18next';

const { ORGANIZATION, REVSHOP, SUPPLIER } = ShippingAccountTypeEnum;

const pullDownOptions = [
  {
    label: 'Use the same shipping option as the job configuration screen',
    value: false,
  },
  {
    label: 'Display a pulldown to select the shipping account to use',
    value: true,
  },
];
const ShippingAccounts = () => {
  const [accountTypeModel, setAccountTypeModel] =
    useState<OrgShippingAccountTypeDisplay>();
  const [saveLoading, setSaveLoading] = useState(false);
  const orgId = useOrgId();
  const org = useOrg();

  // 根据组织类型动态生成 options
  const options = useMemo(() => {
    const isSaasOrCustomer = org && (isSaasOrg(org) || isCustomerOrg(org));
    return [
      {
        label: 'This organization',
        value: ORGANIZATION,
      },
      {
        label: 'Revshop',
        value: REVSHOP,
        disabled: isSaasOrCustomer,
      },
      {
        label: 'Supplier organization',
        value: SUPPLIER,
      },
    ];
  }, [org]);
  const accountType = useAppSelector(
    (state) => state.shipping.orgShippingConfig
  )?.accountTypeDisplay;

  useEffect(() => {
    if (accountType) {
      setAccountTypeModel(accountType);
    }
  }, [accountType]);
  const [form] = Form.useForm();

  const save = async () => {
    if (!accountTypeModel) return;
    setSaveLoading(true);
    try {
      await form.validateFields();
      await shippingService.updateShippingAccountTypeDisplay(
        orgId,
        accountTypeModel
      );
    } catch {
    } finally {
      setSaveLoading(false);
    }
  };
  return (
    <div className="shipping-accounts">
      <div className="title mb-20">
        Display these shipping accounts for this organization for the various
        roles:
      </div>
      <Form
        initialValues={accountType}
        form={form}
        onValuesChange={async (v, allValues) => {
          setAccountTypeModel(allValues);
        }}
      >
        <RevxFlexLayout align="top" className="display-block mb-20">
          <RevxFlexLayout className="label bold">Buyer</RevxFlexLayout>
          <div className="content">
            <div className="content__label bold">
              For the job configuration shipping screen:
            </div>
            <Form.Item
              label="Display shipping options from this account"
              name={['buyerDisplay', 'normalizedOption']}
            >
              <Radio.Group options={options} />
            </Form.Item>
            <div className="content__tips bold">
              For the pre-configured SKU shipping screen - there is no shipping
              account pulldown available
            </div>
          </div>
        </RevxFlexLayout>
        <RevxFlexLayout align="top" className="display-block mb-20">
          <RevxFlexLayout className="label bold">Sourcer</RevxFlexLayout>
          <div className="content">
            <div className="content__label bold">
              For the job configuration shipping screen:
            </div>
            <Form.Item
              label="Display shipping options from this account"
              name={['sourcerDisplay', 'normalizedOption']}
            >
              <Radio.Group options={options} />
            </Form.Item>
            <div className="content__tips bold">
              For the pre-configured SKU shipping screen and RFQ
            </div>
            <Form.Item name={['sourcerDisplay', 'pullDownAllowed']}>
              <Radio.Group options={pullDownOptions} />
            </Form.Item>
            <RevxFlexLayout
              hidden={!accountTypeModel?.sourcerDisplay?.pullDownAllowed}
              style={{ paddingLeft: 30 }}
            >
              <Form.Item
                name={['sourcerDisplay', 'pullDownOptions']}
                rules={[
                  {
                    required: accountTypeModel?.sourcerDisplay?.pullDownAllowed,
                    message: 'Please select at least one item',
                  },
                ]}
              >
                <Checkbox.Group options={options} />
              </Form.Item>
            </RevxFlexLayout>
          </div>
        </RevxFlexLayout>
        <RevxFlexLayout align="top" className="display-block mb-20">
          <RevxFlexLayout className="label bold">Supplier</RevxFlexLayout>
          <div className="content">
            <div className="content__label bold">
              For the job configuration shipping screen:
            </div>
            <Form.Item
              label="Display shipping options from this account"
              name={['supplierDisplay', 'normalizedOption']}
            >
              <Radio.Group options={options} />
            </Form.Item>
            <div className="content__tips bold">
              For the pre-configured SKU shipping screen
            </div>
            <Form.Item name={['supplierDisplay', 'pullDownAllowed']}>
              <Radio.Group options={pullDownOptions} />
            </Form.Item>
            <RevxFlexLayout
              hidden={!accountTypeModel?.supplierDisplay?.pullDownAllowed}
              style={{ paddingLeft: 30 }}
            >
              <Form.Item
                name={['supplierDisplay', 'pullDownOptions']}
                rules={[
                  {
                    required:
                      accountTypeModel?.supplierDisplay?.pullDownAllowed,
                    message: 'Please select at least one item',
                  },
                ]}
              >
                <Checkbox.Group options={options} />
              </Form.Item>
            </RevxFlexLayout>
          </div>
        </RevxFlexLayout>
      </Form>
      <div className="mb-20">
        <RevxButton
          type="primary"
          onClick={async () => {
            await save();
          }}
          loading={saveLoading}
        >
          <Trans i18nKey="common.save" />
        </RevxButton>
      </div>
    </div>
  );
};
export default ShippingAccounts;
